import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import React, { useCallback, useState } from 'react'

import { ReactComponent as MoneyCheckDollarPenIcon } from 'assets/MoneyCheckDollarPen.svg'
import { ReactComponent as MoneyCheckDollarPlusIcon } from 'assets/MoneyCheckDollarPlus.svg'
import StyledBadge from 'components/notes/common/StyledBadge'
import AddEditPurchaseOrderDialog from 'components/work-order/purchase-order/AddEditPurchaseOrderDialog'
import { useHandleRouteManagerApiPolling } from 'containers/new-route-manager/common/useHandleRouteManagerApiPolling'
import T from 'T'

type Props = {
  workOrderId: string
  serviceDate?: string | null
  configuredServiceId?: string | null
  purchaseOrder?: string | null
  icon?: React.ReactNode
  onSuccess?: () => void
}

// `icon` lets callers swap the glyph (master-sequence cards) without losing the behavior.
const PurchaseOrderBadge: React.FC<Props> = ({
  workOrderId,
  serviceDate = '',
  configuredServiceId = '',
  purchaseOrder = '',
  icon = null,
  onSuccess,
}) => {
  const [viewPopoverAnchorEl, setViewPopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [isOpenAddEditPurchaseOrderDialog, setIsOpenAddEditPurchaseOrderDialog] = useState(false)
  const hasPurchaseOrder = !!purchaseOrder

  const handleViewPopoverOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setViewPopoverAnchorEl(event.currentTarget)
  }, [])
  const handleViewPopoverClose = useCallback(() => setViewPopoverAnchorEl(null), [])
  const handleAddEditPOClick = useCallback(() => setIsOpenAddEditPurchaseOrderDialog(true), [])
  const handleAddEditPOClose = useCallback(() => setIsOpenAddEditPurchaseOrderDialog(false), [])

  useHandleRouteManagerApiPolling(isOpenAddEditPurchaseOrderDialog)

  return (
    <>
      <Tooltip arrow disableInteractive disableHoverListener={hasPurchaseOrder} title={hasPurchaseOrder ? '' : T.PURCHASE_ORDER_LABEL}>
        <IconButton
          size="small"
          onClick={handleAddEditPOClick}
          {...(hasPurchaseOrder && {
            onMouseEnter: handleViewPopoverOpen,
          })}
          onMouseLeave={handleViewPopoverClose}
        >
          <StyledBadge badgeContent={hasPurchaseOrder ? 1 : 0}>
            {icon ?? <SvgIcon inheritViewBox component={hasPurchaseOrder ? MoneyCheckDollarPenIcon : MoneyCheckDollarPlusIcon} />}
          </StyledBadge>
        </IconButton>
      </Tooltip>
      <Popover
        disableRestoreFocus
        anchorEl={viewPopoverAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(viewPopoverAnchorEl)}
        sx={{ pointerEvents: 'none' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleViewPopoverClose}
      >
        <Box px={2}>
          <ListItemText
            primary={purchaseOrder}
            secondary="Purchase order"
            slotProps={{
              primary: { textAlign: 'right', variant: 'h6', fontWeight: 400 },
              secondary: { textAlign: 'right', variant: 'caption' },
            }}
          />
        </Box>
      </Popover>
      <AddEditPurchaseOrderDialog
        configuredServiceId={configuredServiceId ?? ''}
        existingPurchaseOrder={purchaseOrder ?? ''}
        isOpen={isOpenAddEditPurchaseOrderDialog}
        serviceDate={serviceDate ?? ''}
        workOrderId={workOrderId}
        onClose={handleAddEditPOClose}
        onSuccess={onSuccess}
      />
    </>
  )
}

export default PurchaseOrderBadge
